import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { Activity, Calendar, Eye, RefreshCw, Users, UserPlus } from 'lucide-react';
import {
  AccessDashboardResponse,
  getAccessDashboard,
} from '../../services/stats/PageStatsService';
import { toast } from '../../utils/toast';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Filler);

const domainColors: Record<string, string> = {
  ebook: '#2563eb',
  lab: '#059669',
  eng: '#ea580c',
  tronde: '#7c3aed',
};

const compactNumber = (value: number) => new Intl.NumberFormat('vi-VN').format(value || 0);

const StatsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<AccessDashboardResponse | null>(null);
  const [period, setPeriod] = useState<'day' | 'month' | 'year'>('month');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      setDashboard(await getAccessDashboard(period, date));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Không thể tải thống kê truy cập';
      console.error('Error loading access dashboard:', error);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [period, date]);

  const domainLabels = dashboard?.domains.map((item) => item.label) || [];

  const domainBarData = useMemo(() => ({
    labels: domainLabels,
    datasets: [
      {
        label: 'Phiên làm việc',
        data: dashboard?.domains.map((item) => item.sessions) || [],
        backgroundColor: dashboard?.domains.map((item) => domainColors[item.domain]) || [],
        borderRadius: 6,
        maxBarThickness: 48,
      },
      {
        label: 'Tài khoản mới',
        data: dashboard?.domains.map((item) => item.new_accounts) || [],
        backgroundColor: '#94a3b8',
        borderRadius: 6,
        maxBarThickness: 48,
      },
    ],
  }), [dashboard]);

  const ecosystemLineData = useMemo(() => ({
    labels: dashboard?.series.labels || [],
    datasets: [
      {
        label: 'Tổng phiên hệ sinh thái',
        data: dashboard?.series.ecosystem_sessions || [],
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.12)',
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
      {
        label: 'Tổng tài khoản mới',
        data: dashboard?.series.ecosystem_new_accounts || [],
        borderColor: '#16a34a',
        backgroundColor: 'rgba(22, 163, 74, 0.10)',
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
    ],
  }), [dashboard]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    plugins: {
      legend: { position: 'bottom' as const, labels: { boxWidth: 10, usePointStyle: true, padding: 16 } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#64748b' } },
      y: { beginAtZero: true, ticks: { precision: 0, color: '#64748b' }, grid: { color: '#e2e8f0' } },
    },
  }), []);

  const totals = dashboard?.totals || { sessions: 0, page_views: 0, active_accounts: 0, new_accounts: 0 };

  if (!loading && !dashboard) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-white p-12 text-center">
        <div className="mb-4 rounded-full bg-slate-100 p-3">
          <Eye className="h-6 w-6 text-slate-400" />
        </div>
        {error ? (
          <>
            <h3 className="text-lg font-semibold text-red-600">Lỗi tải dữ liệu</h3>
            <p className="mt-1 text-sm text-slate-500">{error}</p>
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-slate-900">Không có dữ liệu</h3>
            <p className="mt-1 text-sm text-slate-500">Chưa có dữ liệu thống kê cho thời kỳ đã chọn.</p>
          </>
        )}
        <button
          onClick={loadDashboard}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4" />
          Tải lại
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold text-blue-600">Access Stats</p>
          <h1 className="text-3xl font-bold text-slate-950">Thống kê sử dụng hệ sinh thái</h1>
          <p className="mt-2 text-sm text-slate-500">
            Theo dõi phiên làm việc thực tế và tài khoản mới cho Ebook, Lab, English và Trộn đề.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-slate-200 bg-white p-1">
            {(['day', 'month', 'year'] as const).map((item) => (
              <button
                key={item}
                onClick={() => setPeriod(item)}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition ${period === item
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
                  }`}
              >
                {item === 'day' ? 'Ngày' : item === 'month' ? 'Tháng' : 'Năm'}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
            <Calendar className="h-4 w-4" />
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className="bg-transparent outline-none"
            />
          </label>
          <button
            onClick={loadDashboard}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Làm mới
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {[
          { label: 'Phiên làm việc', value: totals.sessions, icon: Activity, color: 'text-blue-600 bg-blue-50' },
          { label: 'Tài khoản mới', value: totals.new_accounts, icon: UserPlus, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Tài khoản hoạt động', value: totals.active_accounts, icon: Users, color: 'text-violet-600 bg-violet-50' },
          { label: 'Lượt xem trang', value: totals.page_views, icon: Eye, color: 'text-orange-600 bg-orange-50' },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-500">{item.label}</p>
                <div className={`rounded-md p-2 ${item.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
              {loading ? (
                <div className="mt-3 h-9 w-24 animate-pulse rounded bg-slate-200"></div>
              ) : (
                <p className="mt-3 text-3xl font-bold text-slate-950">{compactNumber(item.value)}</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <section className="xl:col-span-2 rounded-lg border border-slate-200 bg-white p-5">
          <div className="mb-5">
            <h2 className="text-base font-bold text-slate-950">So sánh từng subdomain</h2>
            <p className="text-sm text-slate-500">Cột chính là số phiên làm việc; cột phụ là tài khoản mới.</p>
          </div>
          <div className="h-80 bg-gradient-to-br from-slate-50 to-slate-100 rounded">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="mb-2 h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-blue-600 mx-auto"></div>
                  <p className="text-sm text-slate-500">Đang tải...</p>
                </div>
              </div>
            ) : dashboard ? (
              <Bar data={domainBarData} options={chartOptions} />
            ) : null}
          </div>
        </section>

        <section className="xl:col-span-3 rounded-lg border border-slate-200 bg-white p-5">
          <div className="mb-5">
            <h2 className="text-base font-bold text-slate-950">Tổng hệ sinh thái</h2>
            <p className="text-sm text-slate-500">Xu hướng phiên làm việc và tài khoản mới trong khoảng thời gian đã chọn.</p>
          </div>
          <div className="h-80 bg-gradient-to-br from-slate-50 to-slate-100 rounded">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="mb-2 h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-blue-600 mx-auto"></div>
                  <p className="text-sm text-slate-500">Đang tải...</p>
                </div>
              </div>
            ) : dashboard ? (
              <Line data={ecosystemLineData} options={chartOptions} />
            ) : null}
          </div>
        </section>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-bold text-slate-950">Chi tiết theo subdomain</h2>
        </div>
        {loading ? (
          <div className="flex h-64 items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
            <div className="text-center">
              <div className="mb-2 h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-blue-600 mx-auto"></div>
              <p className="text-sm text-slate-500">Đang tải bảng chi tiết...</p>
            </div>
          </div>
        ) : dashboard?.domains && dashboard.domains.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Subdomain</th>
                  <th className="px-5 py-3 text-right">Phiên</th>
                  <th className="px-5 py-3 text-right">Tài khoản mới</th>
                  <th className="px-5 py-3 text-right">Tài khoản hoạt động</th>
                  <th className="px-5 py-3 text-right">Lượt xem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dashboard.domains.map((item) => (
                  <tr key={item.domain} className="hover:bg-slate-50 transition">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: domainColors[item.domain] }} />
                        <div>
                          <p className="font-semibold text-slate-900">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.domain}.vatli365.vn</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right font-semibold">{compactNumber(item.sessions)}</td>
                    <td className="px-5 py-4 text-right">{compactNumber(item.new_accounts)}</td>
                    <td className="px-5 py-4 text-right">{compactNumber(item.active_accounts)}</td>
                    <td className="px-5 py-4 text-right">{compactNumber(item.page_views)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex h-32 items-center justify-center text-center text-sm text-slate-500">
            Không có dữ liệu để hiển thị
          </div>
        )}
      </section>
    </div>
  );
};

export default StatsDashboard;
